import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import { usePermissions } from '../../hooks/usePermissions.js';
import {
  LayoutDashboard,
  Users,
  Droplets,
  Box,
  SprayCan,
  ClipboardList,
  FileText,
  Shield,
  MapPin,
  FolderCog,
  Star,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Receipt
} from 'lucide-react';
import logo from "../../static/images/logo.jpeg";

const Sidebar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [openDropdown, setOpenDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading } = useAuth();
  const { permissions, isClient, isSurveyInspector, isAccountant } = usePermissions();

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsOpen(true);
      } else {
        setIsOpen(false);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!user) return;

    const isSystemAdmin = user.role === 'System Admin';
    const isAdmin = ['System Admin', 'Admin'].includes(user.role);
    const isAdminOrAccountant = ['System Admin', 'Admin', 'Accountant'].includes(user.role);

    // Determine dashboard path based on role
    const getDashboardPath = () => {
      switch (user.role) {
        case 'Documentation Manager':
          return '/documentation-manager-dashboard';
        case 'Survey Inspector':
          return '/survey-inspector-dashboard';
        case 'Client':
          return '/client-dashboard';
        case 'Accountant':
          return '/accountant-dashboard';
        default:
          return '/admin-dashboard';
      }
    };

    const items = [
      {
        label: 'Dashboard',
        icon: LayoutDashboard,
        path: getDashboardPath(),
        visible: true,
        type: 'single'
      },
      { 
        label: 'Users', 
        icon: Users, 
        path: '/users', 
        visible: permissions.users.canView && !isClient && !isSurveyInspector,
        type: 'single' 
      },
      { 
        label: 'Billings', 
        icon: Receipt, 
        path: '/billings', 
        visible: permissions.billing.canView, 
        type: 'single' 
      },
      { 
        label: 'Tanks', 
        icon: Droplets, 
        path: '/tanks', 
        visible: permissions.tanks.canView && !isClient && !isSurveyInspector, // Restrict for clients and survey inspectors
        type: 'single' 
      },
      { 
        label: 'Containers', 
        icon: Box, 
        path: '/containers', 
        visible: permissions.containers.canView && !isClient && !isSurveyInspector, // Restrict for clients and survey inspectors
        type: 'single' 
      },
      { 
        label: 'Tank Cleanings', 
        icon: FileText,
        path: '/tank-cleanings', 
        visible: permissions.tankCleaning.canView, 
        type: 'single' 
      },
      { 
        label: 'Dry Box Surveys', 
        icon: FileText, 
        path: '/dry-box-surveys', 
        visible: permissions.dryBoxSurvey.canView, 
        type: 'single' 
      },
      { 
        label: 'OnHires', 
        icon: FileText, 
        path: '/onhires', 
        visible: permissions.onHire.canView, 
        type: 'single' 
      },
      { 
        label: 'EI Receipts', 
        icon: FileText, 
        path: '/ei-receipts', 
        visible: permissions.eiReceipt.canView, 
        type: 'single' 
      },
      { 
        label: 'TI Reports', 
        icon: FileText, 
        path: '/ti-reports', 
        visible: permissions.tiReport.canView, 
        type: 'single' 
      },
      { 
        label: 'Locations', 
        icon: MapPin, 
        path: '/locations', 
        visible: permissions.locations.canView, 
        type: 'single' 
      },
      // { 
      //   label: 'Roles', 
      //   icon: Shield, 
      //   path: '/roles', 
      //   visible: permissions.roles.canView, 
      //   type: 'single' 
      // },
      {
        label: 'Content Management',
        icon: FolderCog,
        visible: permissions.tanks.canView && !isClient && !isSurveyInspector && !isAccountant,
        type: 'dropdown',
        children: [
          { 
            label: 'Testimonials', 
            icon: Star, 
            path: '/testimonials', 
            visible: permissions.testimonials.canView 
          },
          { 
            label: 'Contact Messages', 
            icon: MessageSquare, 
            path: '/contact', 
            visible: permissions.contact.canView 
          }
        ]
      }
    ];

    setMenuItems(items.filter(i => i.visible));
  }, [user, permissions, isClient, isSurveyInspector, isAccountant]);

  useEffect(() => {
    if (user && isOpen) {
      const activeDropdown = menuItems.find(item => {
        if (item.type === 'dropdown') {
          return item.children?.some(child => child.visible && isActive(child.path));
        }
        return false;
      });

      if (activeDropdown && openDropdown !== activeDropdown.label) {
        setOpenDropdown(activeDropdown.label);
      }
    }
  }, [location.pathname, menuItems, user, isOpen]);

  const isActive = (path) => location.pathname.startsWith(path);

  const isDropdownActive = (children) => {
    return children?.some(child => child.visible && isActive(child.path));
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) {
      setIsOpen(false);
    }
  };

  const toggleDropdown = (label) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  if (loading || !user) {
    return (
      <>
        <aside className="w-16 lg:w-20 bg-dark text-white h-screen fixed left-0 top-0 z-20">
          <div className="p-3 lg:p-4 border-b border-gray-700 flex items-center justify-between">
            <button className="p-2 hover:bg-gray-700 rounded-md transition-colors text-lg">
              ☰
            </button>
          </div>
        </aside>
        <div className="ml-16 lg:ml-20 transition-all duration-300" />
      </>
    );
  }

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      <aside className={`${isOpen? "w-full lg:w-80" : "w-16 lg:w-20"} transition-all duration-300 h-screen overflow-y-auto text-dark ${isOpen ? "bg-white" : "bg-transparent lg:bg-white"} absolute left-0 top-0 z-[99] lg:static lg:top-auto lg:left-auto lg:z-auto bg-transparent`}>

        <div className={`p-3 lg:p-4 flex items-center justify-between lg:hidden fixed bg-white ${isOpen ? "fixed top-0 left-0 w-full h-[60px] z-[100]" : ""}`}>
          {isOpen && (
            <h1 className="lg:hidden">
              <img src={logo} alt="Logo" className="h-10 w-auto" />
            </h1>
          )}
          <button onClick={() => setIsOpen(!isOpen)} className="p-1 text-lg lg:hidden">
            {isOpen ? "X" : "☰"}
          </button>
        </div>

        <nav
          className={`p-2 lg:p-4 space-y-1 lg:space-y-2 fixed bg-white h-[100vh] w-[90%] lg:w-[265px]
          ${isOpen ? "block" : "hidden lg:block"}
          ${isOpen ? "mt-[60px] lg:mt-0" : "mt-0"}`}
        >
          {menuItems.map((item) => {
            const Icon = item.icon;

            if (item.type === 'single') {
              const active = isActive(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavigation(item.path)}
                  className={`w-full flex items-center gap-3 px-3 lg:px-1 py-2.5 lg:py-1.5 rounded-sm transition-colors text-sm lg:text-base ${
                    active
                      ? 'bg-primary text-secondary font-semibold'
                      : 'text-secondary hover:bg-primary hover:font-regular'
                  }`}
                  title={item.label}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {isOpen && <span className="truncate">{item.label}</span>}
                </button>
              );
            }

            if (item.type === 'dropdown') {
              const dropdownActive = isDropdownActive(item.children);
              const isDropdownOpen = openDropdown === item.label;

              return (
                <div key={item.label}>
                  <button
                    onClick={() => {
                      if (!isOpen) {
                        setIsOpen(true);
                        setTimeout(() => {
                          setOpenDropdown(item.label);
                        }, 100);
                      } else {
                        toggleDropdown(item.label);
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-3 lg:px-1 py-2.5 lg:py-1.5 rounded-sm transition-colors text-sm lg:text-base ${
                      dropdownActive
                        ? 'bg-primary text-secondary font-semibold'
                        : 'text-secondary hover:bg-primary hover:font-regular'
                    }`}
                    title={item.label}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {isOpen && (
                      <>
                        <span className="truncate flex-1 text-left">{item.label}</span>
                        <ChevronDown className={`w-5 h-5 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
                      </>
                    )}
                  </button>

                  {isOpen && isDropdownOpen && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.children.filter(child => child.visible).map((child) => {
                        const ChildIcon = child.icon;
                        const childActive = isActive(child.path);

                        return (
                          <button
                            key={child.path}
                            onClick={() => handleNavigation(child.path)}
                            className={`w-full flex items-center gap-2 px-3 lg:px-1 py-2 lg:py-1.5 rounded-sm transition-colors text-sm lg:text-base ${
                              childActive
                                ? 'text-secondary font-semibold'
                                : 'text-secondary hover:font-semibold'
                            }`}
                            title={child.label}
                          >
                            <ChildIcon className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{child.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            return null;
          })}
        </nav>
      </aside>
    </>
  );
};

export default Sidebar;
